import { useState } from "react";
import {
    Button,
    TextField,
    Box,
    Typography,
    Paper,
    CircularProgress,
    IconButton,
    InputAdornment
} from "@mui/material";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api.js';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => event.preventDefault();

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setMessage("");
        setLoading(true);

        if (!password || !confirmPassword) {
            setError("Preencha todos os campos.");
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError("As senhas não conferem.");
            setLoading(false);
            return;
        }

        try {
            await api.post('/auth/reset-password', { token, newPassword: password });
            setMessage("Senha redefinida com sucesso! Redirecionando...");
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            console.error("Erro ao redefinir senha:", err);
            if (err.response?.data?.error) {
                setError(err.response.data.error);
            } else {
                setError("Erro ao redefinir senha. O link pode ter expirado.");
            }
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#000' }}>
                <Typography color="error">Token inválido ou não fornecido.</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#000000',
                padding: 2,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: 'url(/fundo_login.png?v=6)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    opacity: 0.5,
                    zIndex: 0,
                }}
            />
            <Paper
                elevation={8}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    padding: 4,
                    borderRadius: 3,
                    maxWidth: 400,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h5" component="h1" gutterBottom align="center">
                    Nova Senha
                </Typography>

                {message && (
                    <Typography color="success.main" sx={{ mb: 2, textAlign: 'center' }}>
                        {message}
                    </Typography>
                )}

                {error && (
                    <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
                        {error}
                    </Typography>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                    <TextField
                        margin="normal"
                        fullWidth
                        name="password"
                        label="Nova Senha"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading || !!message}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="alternar visibilidade da senha"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        name="confirmPassword"
                        label="Confirmar Nova Senha"
                        type={showPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading || !!message}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ mt: 3, mb: 2, py: 1.5, bgcolor: '#20ac9c', '&:hover': { bgcolor: '#167a6f' } }}
                        disabled={loading || !!message}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Redefinir Senha'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
